/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useState } from 'react';
import type { ModalProps } from './modules/AddEditStudentModal';
import type { AxiosError } from 'axios';
import type { RosterManagementStudentListItem } from '@whizz/api-model';
import type { ContinueActionModalProps, NotEnoughLicensesModalProps, ResetAssessmentModalProps } from './Students.components';
import { useI18n } from '@whizz/react-i18n';
import { NewButton } from '../../../../global';
import { AddEditStudentModal, StudentsTable } from './modules';
import { useModal } from '../../../../hooks/useModal';
import { useSuccessErrorModal } from '../../../../hooks/useSuccessErrorModal';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { model, repository } from '@whizz/api-client';
import { MUTATIONS_IDS, QUERIES_IDS } from '../../../../util/vars';
import useStudentActionSelector from '../../../../hooks/filters/useStudentActionSelector';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { ContinueActionModal, NotEnoughLicensesModal, ResetAssessmentModal } from './Students.components';
import RosterManagementLayout from '../../../../layouts/RosterManagementLayout';
import Loader from '../../../../components/loader/Loader';
import { API_ERRORS } from '../../../../util/constants';
import StudentsPassword from '../../../../global/span/StudentsPassword';
import { staffEmptyData } from '../Staff/Staff';
import { useCleverIntegration } from '../../../../hooks/useCleverIntegration';

export type ActionType = {
  type: 'created' | 'delta',
  studentId: number,
};

const Students = (): JSX.Element => {
  const { translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const queryClient = useQueryClient();
  const [ ActionSelector, actionValue, extraData, resetStudentAction, filtersEnabled, isActionExtraDataValid, Filters ] = useStudentActionSelector();
  const [ showContinue, setShowContinue ] = useState(true);
  const [ modal, toggle, setProps ] = useModal(
    (props: ModalProps) => (
      <AddEditStudentModal
        {...props}
        onNotEnoughSubscriptions={() => {
          setNotEnoughLicensesModalProps((prev) => ({
            ...prev,
            studentsCount: 1,
            remainingLicensesCount: 0,
          }));
          toggleNotEnoughLicensesModal(true);
        }}
        onStudentDuplicate={onDuplicate}
      />
    ),
  );
  const [ resetAssessmentModal, toggleResetAssessment, setResetAssessmentProps ] = useModal((props: ResetAssessmentModalProps) => (
    <ResetAssessmentModal {...props} />
  ));

  const [ continueActionModal, toggleContinueActionModal, setContinueActionModalProps ] = useModal(
    (props: ContinueActionModalProps) => (
      <ContinueActionModal
        {...props}
        action={actionValue!}
        extraData={extraData}
        onConfirm={(studentsIds: number[]) => studentsContinueMutation.mutate({ selectedIds: studentsIds })}
        setShowContinue={setShowContinue}
      />
    ),
  );
  const [ notEnoughLicensesModal, toggleNotEnoughLicensesModal, setNotEnoughLicensesModalProps ] = useModal(
    (props: NotEnoughLicensesModalProps) => (<NotEnoughLicensesModal {...props} />),
    {
      title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.NOT_ENOUGH_LICENSES_MODAL.TITLE'),
      disableMaskClick: true,
    },
  );

  const [ successErrorModal, toggleMessageType ] = useSuccessErrorModal();

  const onSuccess = (actionType?: ActionType) => {
    switch (actionType?.type) {
      case 'created':
        model.Student.one(actionType.studentId!)
          .then((student) => {
            toggleMessageType('success', {
              title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_STUDENT_CREATED_SUCCESS.TITLE'),
              body: (
                <React.Fragment>
                  <p className={'mb-md'}>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTION_STUDENT_CREATED_SUCCESS.CONTENT')}</p>
                  <ul className={'list-none space-y-2'}>
                    <li>{translate('components.TABLES.LOGIN_DETAILS.USERNAME')} <b>{student.username}</b></li>
                    <li>{translate('components.TABLES.LOGIN_DETAILS.PASSWORD')} <b><StudentsPassword studentId={student.id as number}/></b></li>
                    <li>{translate('components.TABLES.LOGIN_DETAILS.PIN')} <b><StudentsPassword type={'pin'} studentId={student.id as number}/></b></li>
                  </ul>
                </React.Fragment>
              ),
            });
          });
        break;
      case 'delta':
        setResetAssessmentProps({
          title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.RESET_ASSESSMENT.title'),
          size: 'md',
          studentId: actionType.studentId!,
          onSuccess: onResetAssessment,
        });
        toggleResetAssessment(true);
        break;
      default:
        toggleMessageType('success');
        break;
    }
  };

  const onError = () => toggleMessageType('error');
  const onDuplicate = () => toggleMessageType('duplicate');
  const onResetAssessment = () => toggleMessageType('success', {
    title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.RESET_ASSESSMENT_SUCCESS.TITLE'),
    body: <p>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.RESET_ASSESSMENT_SUCCESS.CONTENT')}</p>,
  });

  const continueActionModalTitle = (): string | null => {
    switch (actionValue) {
      case RosterManagementStudentActionEnum.moveToClass:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.MOVE_TO_CLASS.TITLE');
      case RosterManagementStudentActionEnum.addToExtraClass:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ADD_TO_EXTRA_CLASS.TITLE');
      case RosterManagementStudentActionEnum.removeFromClass:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.REMOVE_FROM_CLASS.TITLE');
      case RosterManagementStudentActionEnum.moveToYearGroup:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.MOVE_TO_YEAR_GROUP.TITLE');
      case RosterManagementStudentActionEnum.activate:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ACTIVATE.TITLE');
      case RosterManagementStudentActionEnum.deactivate:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.DEACTIVATE.TITLE');
      case RosterManagementStudentActionEnum.archive:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ARCHIVE.TITLE');
      case RosterManagementStudentActionEnum.unarchive:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.UNARCHIVE.TITLE');
      case RosterManagementStudentActionEnum.completelyDeleteArchived:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.COMPLETELY_DELETE_ARCHIVED.TITLE');
      case RosterManagementStudentActionEnum.addTags:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.ADD_TAG.TITLE');
      case RosterManagementStudentActionEnum.removeTags:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.CONTINUE_MODAL.REMOVE_TAG.TITLE');
      default:
        return null;
    }
  };

  const studentsStatisticsQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ],
    () => repository.RosterManagementRepository.studentsStatistics(),
  );

  const studentsQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS, actionValue, extraData.class?.value, extraData.toClass?.value, extraData.yearGroup?.value, extraData.tag?.value ],
    () => repository.StudentRepository.rosterManagementListItemsByAction(
      actionValue as RosterManagementStudentActionEnum,
      {
        classId: extraData.class?.value as number,
        toClassId: extraData.toClass?.value as number,
        yearGroupId: extraData.yearGroup?.value as number | null,
        tagId: extraData.tag?.value as number,
      },
    ),
    {
      enabled: isActionExtraDataValid,
      placeholderData: [],
    },
  );

  const staffQuery = useQuery(
    [ QUERIES_IDS.STAFF ],
    () => repository.StaffRepository.getStaff(),
    {
      enabled: true,
      placeholderData: staffEmptyData,
    },
  );

  const studentsContinueMutation = useMutation(
    [ MUTATIONS_IDS.STUDENTS_ACTIONS ],
    ({ selectedIds }: { selectedIds: number[] }) => {
      switch (actionValue) {
        case RosterManagementStudentActionEnum.activate:
          return repository.StudentRepository.activate(selectedIds);
        case RosterManagementStudentActionEnum.deactivate:
          return repository.StudentRepository.deactivate(selectedIds);
        case RosterManagementStudentActionEnum.archive:
          return repository.StudentRepository.archive(selectedIds);
        case RosterManagementStudentActionEnum.unarchive:
          return repository.StudentRepository.unarchive(
            selectedIds,
            extraData.class!.value,
          );
        case RosterManagementStudentActionEnum.addToExtraClass:
          return repository.StudentRepository.addToClass(selectedIds, extraData.class!.value);
        case RosterManagementStudentActionEnum.removeFromClass:
          return repository.StudentRepository.removeFromClass(selectedIds, extraData.class!.value);
        case RosterManagementStudentActionEnum.moveToClass:
          return repository.StudentRepository.moveToClass(
            selectedIds,
            extraData.class!.value,
            extraData.toClass!.value,
            extraData.moveYearGroup,
          );
        case RosterManagementStudentActionEnum.moveToYearGroup:
          return repository.StudentRepository.moveToYearGroup(selectedIds, extraData.toYearGroup!.value as number);
        case RosterManagementStudentActionEnum.addTags:
          return repository.StudentRepository.addTagToStudents(selectedIds, extraData.tag!.value);
        case RosterManagementStudentActionEnum.removeTags:
          return repository.StudentRepository.removeTagFromStudents(selectedIds, extraData.tag!.value);
        case RosterManagementStudentActionEnum.completelyDeleteArchived:
          return repository.StudentRepository.completelyDeleteArchived(selectedIds);
      }

      return Promise.resolve();
    },
    {
      onSuccess: () => {
        if (RosterManagementStudentActionEnum.edit !== actionValue) {
          resetStudentAction();
        }

        queryClient.removeQueries(QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS, { exact: false });
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.STUDENTS_STATISTICS ]);
        queryClient.invalidateQueries([ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES ]);
        onSuccess();
      },
      onError: (error: AxiosError<{ error: string, context: { needed: number, remaining: number } }>) => {
        if (null == error.response) {
          return;
        }

        if (API_ERRORS.NOT_ENOUGH_SUBSCRIPTIONS === error.response.data.error) {
          setNotEnoughLicensesModalProps((prev) => ({
            ...prev,
            studentsCount: error.response!.data.context.needed,
            remainingLicensesCount: error.response!.data.context.remaining,
          }));
          toggleNotEnoughLicensesModal(true);
        } else {
          onError();
        }

        toggle(false);
      },
    },
  );

  const handleAdd = () => {
    setProps({
      title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.ADD_TITLE'),
      disableMaskClick: true,
      onSuccess,
      onError,
    });
    toggle(true);
  };

  const handleEdit = (student: RosterManagementStudentListItem) => {
    setProps({
      title: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ADD_EDIT.EDIT_TITLE'),
      student: student,
      disableMaskClick: true,
      onSuccess,
      onError,
    });
    toggle(true);
  };

  const filteredStudentsFunction = () => {
    let filteredStudents = studentsQuery.data!;

    if (null != extraData.search) {
      filteredStudents = filteredStudents
        .filter((student) => {
          const lowerSearch = extraData.search!.toLowerCase();
          const lowerFirstName = (student.firstName ?? '').toLowerCase();
          const lowerLastName = (student.lastName ?? '').toLowerCase();

          return `${lowerFirstName} ${lowerLastName}`.includes(lowerSearch)
            || `${lowerLastName}, ${lowerFirstName}`.includes(lowerSearch);
        })
      ;
    }

    if (!filtersEnabled) {
      return filteredStudents;
    }

    if (null != extraData.localYearGroup?.value) {
      filteredStudents = filteredStudents.filter((student) => student.yearGroupId === extraData.localYearGroup?.value);
    }

    if (null != extraData.localClass?.value) {
      filteredStudents = filteredStudents.filter((student) => student.classes?.some((c) => c.id === extraData.localClass?.value));
    }

    if (null != extraData.localTag?.value) {
      filteredStudents = filteredStudents.filter((student) => student.tags?.some((t) => t.id === extraData.localTag?.value));
    }

    if (null != extraData.active) {
      filteredStudents = filteredStudents.filter((student) => student.active === Boolean(extraData.active?.value));
    }

    return filteredStudents;
  };

  const filteredStudents = isActionExtraDataValid ? filteredStudentsFunction() : [];
  const emailsubject = encodeURIComponent(translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.EMAIL_SUBJECT'));
  const SUBSCRIPTIONS_CONTACT_EMAIL = translate('global.SUPPORT_EMAIL');
  const SUBSCRIPTIONS_CONTACT_EMAIL_CC = translate('global.SUPPORT_EMAIL_CC');
  const mailtoCc = SUBSCRIPTIONS_CONTACT_EMAIL_CC ? `&cc=${encodeURIComponent(SUBSCRIPTIONS_CONTACT_EMAIL_CC)}` : '';

  const Statistics = () => {
    if (!studentsStatisticsQuery.isFetched || studentsStatisticsQuery.isFetching) {
      return <Loader disableLabel smallSize />;
    }

    return (
      <>
        <span>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.TOTAL_STUDENTS')}: {studentsStatisticsQuery.data?.totalStudents}</span>
        <span>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.TOTAL_SUBSCRIPTIONS')}:  {studentsStatisticsQuery.data?.totalSubscriptions}</span>
        <span>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.ACTIVE_SUBSCRIPTIONS')}:  {studentsStatisticsQuery.data?.activeSubscriptions}</span>
        <span>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.AVAILABLE_SUBSCRIPTIONS')}:  {studentsStatisticsQuery.data?.availableSubscriptions}</span>
        <span>{translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.NEED_MORE_SUBSCRIPTIONS')}{' '}
          <a
            href={`mailto:${SUBSCRIPTIONS_CONTACT_EMAIL}?subject=${emailsubject}${mailtoCc}`}
            className={'text-primary underline'}
            target={'_blank'}
            rel={'noopener noreferrer'}
          >
            {translate('pages.ROSTER_MANAGEMENT.STUDENTS.STATISTICS.CONTACT_LINK_TEXT')}
          </a>
        </span>
      </>
    );
  };

  const topMessage = () => {
    if (!isActionExtraDataValid || !studentsQuery.isFetched || studentsQuery.isFetching) {
      return null;
    }

    switch (actionValue) {
      case RosterManagementStudentActionEnum.addToExtraClass:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.ADD_TO_EXTRA_CLASS', { count: filteredStudents.length, toClass: extraData.class?.label });
      case RosterManagementStudentActionEnum.removeFromClass:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.REMOVE_FROM_CLASS', { count: filteredStudents.length, fromClass: extraData.class?.label });
      case RosterManagementStudentActionEnum.moveToClass:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.MOVE_TO_CLASS', { count: filteredStudents.length, fromClass: extraData.class?.label, toClass: extraData.toClass?.label });
      case RosterManagementStudentActionEnum.activate:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.ACTIVATE', { count: filteredStudents.length });
      case RosterManagementStudentActionEnum.deactivate:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.DEACTIVATE', { count: filteredStudents.length });
      case RosterManagementStudentActionEnum.addTags:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.ADD_TAG', { count: filteredStudents.length, tag: extraData.tag?.label });
      case RosterManagementStudentActionEnum.removeTags:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.REMOVE_TAG', { count: filteredStudents.length, tag: extraData.tag?.label });
      case RosterManagementStudentActionEnum.archive:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.ARCHIVE', { count: filteredStudents.length });
      case RosterManagementStudentActionEnum.unarchive:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.UNARCHIVE', { count: filteredStudents.length, toClass: extraData.class?.label });
      case RosterManagementStudentActionEnum.completelyDeleteArchived:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.COMPLETELY_DELETE_ARCHIVED', { count: filteredStudents.length });
      case RosterManagementStudentActionEnum.moveToYearGroup:
        return translate('pages.ROSTER_MANAGEMENT.STUDENTS.MESSAGES.MOVE_TO_YEAR_GROUP', { count: filteredStudents.length, fromYearGroup: extraData.yearGroup?.label });
    }

    return null;
  };

  return (
    <RosterManagementLayout
      statistics={<Statistics />}
    >
      {modal}
      {resetAssessmentModal}
      {successErrorModal}
      {notEnoughLicensesModal}
      {continueActionModal}
      <div className={'grid grid-cols-5 gap-x-2 mb-6'}>
        <div className={'col-span-4 space-y-4'}>
          {ActionSelector}
        </div>
        <NewButton
          onClick={handleAdd}
          className={'justify-self-end self-start'}
          data-cy={'rm-new-student'}
          disabled={false}
        >
          {translate('pages.ROSTER_MANAGEMENT.STUDENTS.BUTTONS.ADD')}
        </NewButton>
      </div>
      {null != topMessage() && (
        <div className={'mb-6'}>
          {topMessage()}
        </div>
      )}
      {actionValue && (<StudentsTable
        isLoading={studentsQuery.isFetching}
        students={filteredStudents}
        baseStudents={studentsQuery.data!}
        onEdit={handleEdit}
        onContinueWithSelected={(students) => {
          toggleContinueActionModal(true);
          setContinueActionModalProps((prev) => ({
            ...prev,
            title: continueActionModalTitle(),
            students: students,
            disableMaskClick: true,
          }));
        }}
        isContinueLoading={studentsContinueMutation.isLoading}
        action={actionValue as RosterManagementStudentActionEnum}
        showContinue={showContinue}
        setShowContinue={setShowContinue}
        customHeaderComponent={Filters}
      />)}
    </RosterManagementLayout>
  );
};

export default Students;
